import React, { useState, useEffect, useCallback } from 'react';
import {
  Inbox, Filter, Search, RefreshCw, Loader2, X, Download, Mail, Phone,
  MapPin, Briefcase, Globe, Linkedin, User, Calendar, FileText, ExternalLink,
  CheckCircle2, XCircle, Star, Sparkles, AlertTriangle, MessageSquare,
  BookmarkPlus, Bookmark, Trash2, CheckSquare, Square,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS = [
  { key: 'all', label: 'All', color: 'text-white/60', bg: 'bg-white/[0.04]', border: 'border-white/10' },
  { key: 'new', label: 'New', color: 'text-[#00E5FF]', bg: 'bg-[#00E5FF]/[0.08]', border: 'border-[#00E5FF]/25' },
  { key: 'reviewed', label: 'Reviewed', color: 'text-amber-300', bg: 'bg-amber-500/[0.08]', border: 'border-amber-500/25' },
  { key: 'shortlisted', label: 'Shortlisted', color: 'text-[#c39be5]', bg: 'bg-[#8A2BE2]/[0.10]', border: 'border-[#8A2BE2]/30' },
  { key: 'hired', label: 'Hired', color: 'text-emerald-300', bg: 'bg-emerald-500/[0.10]', border: 'border-emerald-500/30' },
  { key: 'rejected', label: 'Rejected', color: 'text-red-300', bg: 'bg-red-500/[0.08]', border: 'border-red-500/25' },
];

const VacancyApplicationsPage = ({ getAuthHeaders, currentUser, initialPool }) => {
  const isAllowed = currentUser?.role === 'superadmin' || currentUser?.role === 'executive';
  const isTalentPool = initialPool === 'only';
  const [apps, setApps] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [poolCount, setPoolCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (isTalentPool) params.set('pool', 'only');
      const q = params.toString() ? `?${params.toString()}` : '';
      const r = await axios.get(`${BACKEND_URL}/api/admin/vacancy-applications${q}`, { headers });
      setApps(r.data.applications || []);
      setCounts(r.data.counts || {});
      setTotal(r.data.total || 0);
      setPoolCount(r.data.pool_count || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [filter, getAuthHeaders, isTalentPool]);

  useEffect(() => { if (isAllowed) load(); }, [isAllowed, load]);

  const filtered = apps.filter(a => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return [a.full_name, a.email, a.vacancy_title, a.location, a.roblox_username].some(v => (v || '').toLowerCase().includes(s));
  });

  const setStatus = async (appId, newStatus) => {
    setBusy(true);
    try {
      const headers = getAuthHeaders();
      await axios.patch(`${BACKEND_URL}/api/admin/vacancy-applications/${appId}`, { status: newStatus }, { headers });
      toast.success(`Marked as ${newStatus}`);
      setSelected(prev => prev ? { ...prev, status: newStatus } : null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const deleteApp = async (appId) => {
    if (!window.confirm('Permanently delete this application?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/admin/vacancy-applications/${appId}`, { headers });
      toast.success('Application deleted');
      setSelected(null);
      load();
    } catch { toast.error('Delete failed'); }
  };

  // ── Selection helpers ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = (visibleApps) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visibleApps.forEach(a => next.add(a.id));
      return next;
    });
  };

  const runBulk = async (action, opts = {}) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (opts.confirm && !window.confirm(opts.confirm)) return;
    setBulkBusy(true);
    try {
      const headers = getAuthHeaders();
      const r = await axios.post(`${BACKEND_URL}/api/admin/vacancy-applications/bulk`,
        { app_ids: ids, action }, { headers });
      const updated = r.data.updated ?? r.data.deleted ?? 0;
      toast.success(`${opts.label || action} · ${updated} application${updated === 1 ? '' : 's'}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkEmail = () => {
    // Build a mailto link with all selected applicant emails as BCC (safer for privacy)
    const ids = Array.from(selectedIds);
    const rows = apps.filter(a => ids.includes(a.id));
    const emails = rows.map(r => r.email).filter(Boolean);
    if (emails.length === 0) return toast.error('No emails on the selected applications');
    const bcc = emails.join(',');
    const subject = encodeURIComponent('An update from Starnight Holdings');
    window.location.href = `mailto:?bcc=${bcc}&subject=${subject}`;
    toast.success(`Opening email to ${emails.length} applicant${emails.length === 1 ? '' : 's'}`);
  };

  if (!isAllowed) {
    return (
      <div className="flex items-center justify-center h-[70vh]" data-testid="applications-denied">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <h2 className="text-xl font-light text-white tracking-tight mb-1">Restricted</h2>
          <p className="text-white/40 text-sm">Only executives and superadmins can review vacancy applications.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8" data-testid="applications-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0A12] p-6">
        <div className="pointer-events-none absolute top-0 right-0 w-[400px] h-[200px] bg-emerald-500/[0.05] rounded-full blur-[120px] -translate-y-1/4 translate-x-1/4" />
        <div className="pointer-events-none absolute bottom-0 left-0 w-[300px] h-[180px] bg-[#00E5FF]/[0.05] rounded-full blur-[100px] translate-y-1/4 -translate-x-1/4" />
        <div className="relative flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500/[0.15] to-[#00E5FF]/[0.15] border border-white/10 flex items-center justify-center"
              style={{ boxShadow: '0 0 30px rgba(16,185,129,0.15)' }}>
              {isTalentPool ? <Sparkles className="w-4 h-4 text-amber-300" strokeWidth={1.5} /> : <Inbox className="w-4 h-4 text-emerald-300" strokeWidth={1.5} />}
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-medium">Recruitment</p>
              <h1 className="text-2xl font-light text-white tracking-tighter">
                {isTalentPool ? (
                  <>Talent <span className="bg-gradient-to-r from-amber-300 to-rose-400 bg-clip-text text-transparent font-medium">Pool</span></>
                ) : (
                  <>Applications <span className="bg-gradient-to-r from-emerald-300 to-[#00E5FF] bg-clip-text text-transparent font-medium">Inbox</span></>
                )}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] tracking-[0.15em] uppercase text-white/40 tabular-nums">
              {isTalentPool ? `${poolCount} saved` : `${total} total · ${counts.new || 0} new`}
            </span>
            <Button onClick={load} variant="ghost" size="sm" disabled={loading} data-testid="refresh-applications"
              className="h-9 w-9 p-0 rounded-full text-white/40 hover:text-white">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Filter tabs (hidden in Talent Pool mode) */}
        {!isTalentPool && (
          <div className="relative mt-6 flex items-center gap-2 pt-4 border-t border-white/[0.06] flex-wrap">
            {STATUS.map(s => (
              <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`filter-${s.key}`}
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[10px] tracking-[0.12em] uppercase font-medium border transition-all ${
                  filter === s.key
                    ? `${s.bg} ${s.color} ${s.border}`
                    : 'bg-white/[0.02] text-white/40 border-white/[0.06] hover:text-white/70 hover:border-white/[0.12]'
                }`}>
                {s.label}
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] tabular-nums ${filter === s.key ? 'bg-black/30' : 'bg-white/[0.06] text-white/40'}`}>
                  {s.key === 'all' ? total : (counts[s.key] || 0)}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Search + select-all */}
        <div className="relative mt-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="applications-search"
              placeholder={isTalentPool ? "Search the pool by name, email or role…" : "Search by name, email, role, or Roblox username…"}
              className="w-full pl-10 pr-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-full text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#00E5FF]/40 transition-all" />
          </div>
          {filtered.length > 0 && (
            <button
              onClick={() => (selectedIds.size === filtered.length ? clearSelection() : selectAllVisible(filtered))}
              data-testid="select-all-btn"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[10px] tracking-[0.1em] uppercase font-medium bg-white/[0.03] border border-white/[0.06] text-white/60 hover:text-white hover:border-white/[0.14] transition-all"
            >
              {selectedIds.size === filtered.length ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              {selectedIds.size === filtered.length ? 'Clear' : 'Select all'}
            </button>
          )}
        </div>
      </div>

      {/* Bulk action toolbar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-[40] rounded-2xl border border-[#00E5FF]/30 bg-[#0A0A12]/95 backdrop-blur-md px-4 py-2.5 flex items-center gap-2 flex-wrap shadow-lg shadow-[#00E5FF]/10"
          data-testid="bulk-toolbar" style={{ boxShadow: '0 0 40px rgba(0,229,255,0.1)' }}>
          <div className="flex items-center gap-2 mr-2">
            <span className="w-6 h-6 rounded-md bg-[#00E5FF]/15 border border-[#00E5FF]/30 flex items-center justify-center text-[#00E5FF] text-[10px] font-semibold tabular-nums">
              {selectedIds.size}
            </span>
            <span className="text-white text-xs font-medium">selected</span>
          </div>
          <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('shortlist', { label: 'Shortlisted' })} data-testid="bulk-shortlist"
            className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-[#c39be5] bg-[#8A2BE2]/[0.10] border border-[#8A2BE2]/25 hover:bg-[#8A2BE2]/[0.18] disabled:opacity-40">
            <Star className="w-3 h-3 mr-1.5" /> Shortlist
          </Button>
          <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('reviewed', { label: 'Reviewed' })} data-testid="bulk-reviewed"
            className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-amber-300 bg-amber-500/[0.08] border border-amber-500/25 hover:bg-amber-500/[0.15] disabled:opacity-40">
            Mark reviewed
          </Button>
          <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('reject', { label: 'Rejected', confirm: `Reject ${selectedIds.size} application(s)?` })} data-testid="bulk-reject"
            className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-red-300 bg-red-500/[0.08] border border-red-500/25 hover:bg-red-500/[0.15] disabled:opacity-40">
            <XCircle className="w-3 h-3 mr-1.5" /> Reject
          </Button>
          <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('hire', { label: 'Hired', confirm: `Hire ${selectedIds.size} applicant(s)? This will notify them all.` })} data-testid="bulk-hire"
            className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-emerald-300 bg-emerald-500/[0.10] border border-emerald-500/25 hover:bg-emerald-500/[0.18] disabled:opacity-40">
            <CheckCircle2 className="w-3 h-3 mr-1.5" /> Hire
          </Button>
          <div className="w-px h-6 bg-white/[0.08] mx-1" />
          {isTalentPool ? (
            <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('pool_remove', { label: 'Removed from pool' })} data-testid="bulk-pool-remove"
              className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-white/60 bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] disabled:opacity-40">
              <Bookmark className="w-3 h-3 mr-1.5" /> Remove from pool
            </Button>
          ) : (
            <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('pool_add', { label: 'Added to Talent Pool' })} data-testid="bulk-pool-add"
              className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-amber-300 bg-amber-500/[0.08] border border-amber-500/25 hover:bg-amber-500/[0.15] disabled:opacity-40">
              <BookmarkPlus className="w-3 h-3 mr-1.5" /> Save to pool
            </Button>
          )}
          <Button size="sm" disabled={bulkBusy} onClick={bulkEmail} data-testid="bulk-email"
            className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-[#00E5FF] bg-[#00E5FF]/[0.08] border border-[#00E5FF]/25 hover:bg-[#00E5FF]/[0.15] disabled:opacity-40">
            <Mail className="w-3 h-3 mr-1.5" /> Email
          </Button>
          <Button size="sm" disabled={bulkBusy} onClick={() => runBulk('delete', { label: 'Deleted', confirm: `Permanently delete ${selectedIds.size} application(s)? This cannot be undone.` })} data-testid="bulk-delete"
            className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-white/40 hover:text-red-300 bg-transparent border border-white/[0.06] hover:border-red-500/25 disabled:opacity-40">
            <Trash2 className="w-3 h-3 mr-1.5" /> Delete
          </Button>
          <button onClick={clearSelection} data-testid="bulk-clear"
            className="ml-auto text-white/40 hover:text-white text-[10px] tracking-[0.1em] uppercase transition-colors">
            Clear
          </button>
        </div>
      )}

      {/* List */}
      {loading && !apps.length ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.06] bg-[#0A0A12] p-12 text-center">
          {isTalentPool ? <Sparkles className="w-10 h-10 text-white/15 mx-auto mb-3" /> : <Inbox className="w-10 h-10 text-white/15 mx-auto mb-3" />}
          <p className="text-white/40 text-sm">
            {isTalentPool
              ? 'No candidates saved yet. Save promising applicants to revisit when new roles open.'
              : 'No applications match this filter yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="applications-list">
          {filtered.map(app => (
            <ApplicationRow key={app.id} app={app} onOpen={() => setSelected(app)}
              onQuickAction={setStatus} busy={busy}
              selected={selectedIds.has(app.id)}
              onToggleSelect={() => toggleSelect(app.id)} />
          ))}
        </div>
      )}

      {selected && (
        <ApplicationDetailModal
          app={selected}
          onClose={() => setSelected(null)}
          onSetStatus={setStatus}
          onDelete={() => deleteApp(selected.id)}
          onReload={load}
          getAuthHeaders={getAuthHeaders}
          currentUser={currentUser}
          busy={busy}
        />
      )}
    </div>
  );
};

// ─── Row ───
const ApplicationRow = ({ app, onOpen, onQuickAction, busy, selected, onToggleSelect }) => {
  const status = STATUS.find(s => s.key === app.status) || STATUS[1];
  const created = app.created_at ? new Date(app.created_at) : null;
  return (
    <div onClick={onOpen} data-testid={`app-row-${app.id}`}
      className={`group relative rounded-2xl border p-4 backdrop-blur-md cursor-pointer transition-all ${
        selected
          ? 'border-[#00E5FF]/40 bg-[#00E5FF]/[0.04] shadow-lg shadow-[#00E5FF]/10'
          : 'border-white/[0.06] bg-[#0A0A12]/60 hover:border-white/[0.12] hover:bg-[#0A0A12]'
      }`}>
      <div className="flex items-center gap-4">
        {/* Checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          data-testid={`select-${app.id}`}
          className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all flex-shrink-0 ${
            selected
              ? 'bg-[#00E5FF] border-[#00E5FF]'
              : 'bg-white/[0.02] border-white/20 hover:border-white/50'
          }`}
        >
          {selected && <CheckCircle2 className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
        </button>
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-white/[0.04] to-[#0A0A12] border border-white/10 flex items-center justify-center flex-shrink-0">
          <span className="text-white/40 font-light text-sm">{(app.full_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-white text-sm font-medium tracking-tight truncate">{app.full_name}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] tracking-[0.1em] uppercase font-medium border ${status.bg} ${status.border} ${status.color}`}>
              {status.label}
            </span>
            {app.in_talent_pool && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] tracking-[0.1em] uppercase font-medium bg-amber-500/[0.10] border border-amber-500/25 text-amber-300">
                <Sparkles className="w-2.5 h-2.5" /> Pool
              </span>
            )}
            {app.cv && (
              <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.08em] uppercase text-white/40">
                <FileText className="w-2.5 h-2.5" /> CV
              </span>
            )}
          </div>
          <p className="text-white/45 text-xs truncate">
            Applied for <span className="text-white/70">{app.vacancy_title}</span>
          </p>
          <p className="text-white/30 text-[10px] mt-0.5">
            {app.email} {app.location && `· ${app.location}`} {created && `· ${created.toLocaleDateString()} ${created.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
          </p>
        </div>
        <div className="hidden md:flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {app.status !== 'shortlisted' && app.status !== 'hired' && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onQuickAction(app.id, 'shortlisted')} data-testid={`quick-shortlist-${app.id}`}
              className="h-8 px-2.5 rounded-full text-[10px] tracking-[0.1em] uppercase text-[#c39be5]/70 hover:text-[#c39be5] hover:bg-[#8A2BE2]/[0.08]">
              <Star className="w-3 h-3 mr-1" /> Shortlist
            </Button>
          )}
          {app.status !== 'hired' && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onQuickAction(app.id, 'hired')} data-testid={`quick-hire-${app.id}`}
              className="h-8 px-2.5 rounded-full text-[10px] tracking-[0.1em] uppercase text-emerald-300/70 hover:text-emerald-300 hover:bg-emerald-500/[0.08]">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Hire
            </Button>
          )}
          {app.status !== 'rejected' && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => onQuickAction(app.id, 'rejected')} data-testid={`quick-reject-${app.id}`}
              className="h-8 px-2.5 rounded-full text-[10px] tracking-[0.1em] uppercase text-red-300/70 hover:text-red-300 hover:bg-red-500/[0.08]">
              <XCircle className="w-3 h-3 mr-1" /> Reject
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Detail modal with CV preview ───
const ApplicationDetailModal = ({ app: initialApp, onClose, onSetStatus, onDelete, onReload, getAuthHeaders, busy, currentUser }) => {
  const [app, setApp] = useState(initialApp);
  const status = STATUS.find(s => s.key === app.status) || STATUS[1];
  const [cvBlobUrl, setCvBlobUrl] = useState(null);
  const [cvBlobType, setCvBlobType] = useState('');
  const [cvLoading, setCvLoading] = useState(false);
  const [cvError, setCvError] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [activeView, setActiveView] = useState('cv'); // 'cv' | 'profile' | 'notes'
  const [poolBusy, setPoolBusy] = useState(false);
  const notes = app.reviewer_notes || [];

  useEffect(() => { setApp(initialApp); }, [initialApp]);

  const togglePool = async () => {
    setPoolBusy(true);
    try {
      const headers = getAuthHeaders();
      const r = await axios.post(`${BACKEND_URL}/api/admin/vacancy-applications/${app.id}/pool`,
        { in_pool: !app.in_talent_pool }, { headers });
      setApp(r.data);
      toast.success(r.data.in_talent_pool ? 'Saved to Talent Pool' : 'Removed from Talent Pool');
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update Talent Pool');
    } finally {
      setPoolBusy(false);
    }
  };

  const refreshApp = async () => {
    try {
      const headers = getAuthHeaders();
      const r = await axios.get(`${BACKEND_URL}/api/admin/vacancy-applications/${app.id}`, { headers });
      setApp(r.data);
    } catch {}
  };

  // Fetch enriched applicant profile
  useEffect(() => {
    setProfileLoading(true);
    (async () => {
      try {
        const headers = getAuthHeaders();
        const r = await axios.get(`${BACKEND_URL}/api/admin/vacancy-applications/${app.id}/applicant-profile`, { headers });
        setProfile(r.data);
      } catch {
        setProfile(null);
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [app.id]); // eslint-disable-line

  const addNote = async () => {
    const body = noteBody.trim();
    if (!body) return;
    setAddingNote(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/vacancy-applications/${app.id}/notes`, { body }, { headers });
      setNoteBody('');
      toast.success('Note added');
      refreshApp();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/admin/vacancy-applications/${app.id}/notes/${noteId}`, { headers });
      toast.success('Note deleted');
      refreshApp();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  useEffect(() => {
    if (!app.cv) return;
    setCvLoading(true);
    setCvError('');
    (async () => {
      try {
        const headers = getAuthHeaders();
        const res = await axios.get(`${BACKEND_URL}/api/admin/vacancy-applications/${app.id}/cv`, {
          headers, responseType: 'blob',
        });
        // Prefer the blob's own type (server sends inline PDF now)
        const rawType = res.data.type || (app.cv?.mime) || '';
        const finalType = rawType && rawType !== 'application/octet-stream' ? rawType : (app.cv?.mime || 'application/pdf');
        const typedBlob = res.data.type ? res.data : new Blob([res.data], { type: finalType });
        const url = URL.createObjectURL(typedBlob);
        setCvBlobType(finalType);
        setCvBlobUrl(url);
      } catch (e) {
        setCvError(e.response?.data?.detail || 'Could not load CV');
        setCvBlobUrl(null);
      } finally {
        setCvLoading(false);
      }
    })();
    return () => { if (cvBlobUrl) URL.revokeObjectURL(cvBlobUrl); };
  }, [app.id]); // eslint-disable-line

  const isPdf = (cvBlobType || app.cv?.mime || '').includes('pdf');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
      onClick={onClose} data-testid="application-detail-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto bg-[#0A0A12] border border-white/10 rounded-2xl"
        style={{ boxShadow: '0 0 80px rgba(16,185,129,0.1)' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0A0A12]/95 backdrop-blur-md border-b border-white/[0.06] px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {profile?.user_account?.avatar_url ? (
              <img src={profile.user_account.avatar_url} alt=""
                className="w-11 h-11 rounded-full object-cover border border-white/10 flex-shrink-0" />
            ) : (
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-white/[0.04] to-[#0A0A12] border border-white/10 flex items-center justify-center flex-shrink-0">
                <span className="text-white/50 font-light">{(app.full_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}</span>
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <h2 className="text-white text-lg font-medium tracking-tight truncate">{app.full_name}</h2>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[9px] tracking-[0.1em] uppercase font-medium border ${status.bg} ${status.border} ${status.color}`}>
                  {status.label}
                </span>
                {profile?.user_account && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] tracking-[0.1em] uppercase font-medium bg-[#00E5FF]/[0.08] border border-[#00E5FF]/25 text-[#00E5FF]">
                    <User className="w-2.5 h-2.5" /> Member
                  </span>
                )}
              </div>
              <p className="text-white/50 text-xs truncate">
                Applied for {app.vacancy_title}
                {profile?.totals?.applications > 1 && (
                  <span className="ml-2 text-white/30">· {profile.totals.applications} total applications</span>
                )}
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-application-detail"
            className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] flex items-center justify-center text-white/60 hover:text-white flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* View switcher */}
        <div className="sticky top-[73px] z-[9] bg-[#0A0A12]/95 backdrop-blur-md border-b border-white/[0.04] px-4 py-2 flex items-center gap-1 overflow-x-auto no-scrollbar">
          {[
            { id: 'cv', label: 'CV', icon: FileText, count: app.cv ? null : 'None' },
            { id: 'profile', label: 'Profile', icon: User, count: profile?.totals?.applications > 1 ? profile.totals.applications : null },
            { id: 'notes', label: 'Notes', icon: MessageSquare, count: notes.length || null },
          ].map((v) => {
            const active = activeView === v.id;
            const VIcon = v.icon;
            return (
              <button key={v.id} onClick={() => setActiveView(v.id)} data-testid={`view-${v.id}`}
                className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-medium tracking-[0.05em] whitespace-nowrap transition-all ${
                  active ? 'bg-white/[0.06] text-white border border-white/[0.10]' : 'text-white/40 hover:text-white/80 border border-transparent'
                }`}>
                <VIcon className="w-3.5 h-3.5" />
                {v.label}
                {v.count && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold tabular-nums ${active ? 'bg-white/[0.08] text-white/80' : 'bg-white/[0.06] text-white/50'}`}>
                    {v.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-5 gap-0">
          {/* Left: applicant info */}
          <div className="md:col-span-2 border-r border-white/[0.06] p-6 space-y-4">
            <InfoRow icon={Mail} label="Email" value={app.email} link={`mailto:${app.email}`} />
            {app.phone && <InfoRow icon={Phone} label="Phone" value={app.phone} link={`tel:${app.phone}`} />}
            {app.roblox_username && <InfoRow icon={Sparkles} label="Roblox" value={app.roblox_username} />}
            {app.location && <InfoRow icon={MapPin} label="Location" value={app.location} />}
            {app.years_experience && <InfoRow icon={Briefcase} label="Experience" value={app.years_experience} />}
            {app.linkedin_url && <InfoRow icon={Linkedin} label="LinkedIn" value={app.linkedin_url} link={app.linkedin_url} external />}
            {app.portfolio_url && <InfoRow icon={Globe} label="Portfolio" value={app.portfolio_url} link={app.portfolio_url} external />}
            {app.created_at && (
              <InfoRow icon={Calendar} label="Submitted"
                value={new Date(app.created_at).toLocaleString()} />
            )}
            {app.cover_letter && (
              <div className="pt-3 border-t border-white/[0.06]">
                <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-1.5 flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> Cover Letter
                </p>
                <p className="text-white/70 text-xs leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto pr-1">{app.cover_letter}</p>
              </div>
            )}
          </div>

          {/* Right: dynamic content by view */}
          <div className="md:col-span-3 p-6 min-h-[520px]">
            {activeView === 'cv' && (
              <CVPanel
                app={app}
                isPdf={isPdf}
                cvLoading={cvLoading}
                cvError={cvError}
                cvBlobUrl={cvBlobUrl}
              />
            )}
            {activeView === 'profile' && (
              <ApplicantProfilePanel
                profile={profile}
                loading={profileLoading}
                currentAppId={app.id}
              />
            )}
            {activeView === 'notes' && (
              <NotesPanel
                notes={notes}
                noteBody={noteBody}
                setNoteBody={setNoteBody}
                addNote={addNote}
                addingNote={addingNote}
                deleteNote={deleteNote}
                currentUser={currentUser}
              />
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-[#0A0A12]/95 backdrop-blur-md border-t border-white/[0.06] px-6 py-4 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Button onClick={onDelete} variant="ghost" data-testid="delete-application-btn"
              className="h-9 px-3 rounded-full text-[10px] tracking-[0.12em] uppercase text-white/30 hover:text-red-300 hover:bg-red-500/[0.05]">
              Delete
            </Button>
            <Button onClick={togglePool} disabled={poolBusy} data-testid="toggle-pool-btn"
              className={`h-9 px-3 rounded-full text-[10px] tracking-[0.12em] uppercase transition-all ${
                app.in_talent_pool
                  ? 'text-amber-300 bg-amber-500/[0.10] border border-amber-500/25 hover:bg-amber-500/[0.18]'
                  : 'text-white/50 bg-white/[0.03] border border-white/[0.08] hover:text-amber-300 hover:border-amber-500/25'
              }`}>
              {app.in_talent_pool ? <Bookmark className="w-3 h-3 mr-1.5" /> : <BookmarkPlus className="w-3 h-3 mr-1.5" />}
              {poolBusy ? '…' : (app.in_talent_pool ? 'Saved to pool' : 'Save to pool')}
            </Button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => onSetStatus(app.id, 'reviewed')} disabled={busy || app.status === 'reviewed'} data-testid="mark-reviewed-btn"
              className="h-10 px-4 rounded-full text-[10px] tracking-[0.12em] uppercase text-amber-300 bg-amber-500/[0.08] border border-amber-500/25 hover:bg-amber-500/[0.15]">
              Mark Reviewed
            </Button>
            <Button onClick={() => onSetStatus(app.id, 'shortlisted')} disabled={busy || app.status === 'shortlisted'} data-testid="shortlist-btn"
              className="h-10 px-4 rounded-full text-[10px] tracking-[0.12em] uppercase text-[#c39be5] bg-[#8A2BE2]/[0.1] border border-[#8A2BE2]/30 hover:bg-[#8A2BE2]/[0.2]">
              <Star className="w-3 h-3 mr-1.5" /> Shortlist
            </Button>
            <Button onClick={() => onSetStatus(app.id, 'rejected')} disabled={busy || app.status === 'rejected'} data-testid="reject-btn"
              className="h-10 px-4 rounded-full text-[10px] tracking-[0.12em] uppercase text-red-300 bg-red-500/[0.08] border border-red-500/25 hover:bg-red-500/[0.15]">
              <XCircle className="w-3 h-3 mr-1.5" /> Reject
            </Button>
            <Button onClick={() => onSetStatus(app.id, 'hired')} disabled={busy || app.status === 'hired'} data-testid="hire-btn"
              className="h-10 px-5 rounded-full text-[10px] tracking-[0.12em] uppercase font-semibold text-black bg-gradient-to-br from-emerald-300 to-emerald-500 hover:opacity-90 shadow-lg shadow-emerald-500/20">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Hire
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </div>
  );
};

// ─── CV Panel ───
const CVPanel = ({ app, isPdf, cvLoading, cvError, cvBlobUrl }) => {
  if (!app.cv) {
    return (
      <div>
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-3 flex items-center gap-1.5">
          <FileText className="w-3 h-3" /> CV
        </p>
        <div className="rounded-2xl border-2 border-dashed border-white/10 bg-white/[0.02] p-10 text-center">
          <FileText className="w-8 h-8 text-white/15 mx-auto mb-2" />
          <p className="text-white/40 text-sm">No CV attached to this application.</p>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> CV
          </p>
          <p className="text-white/60 text-xs mt-0.5">{app.cv.filename} · {(app.cv.size_bytes / 1024).toFixed(1)} KB</p>
        </div>
        {cvBlobUrl && (
          <div className="flex items-center gap-2">
            <a href={cvBlobUrl} target="_blank" rel="noopener noreferrer" data-testid="cv-open-new-tab"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] text-white/70 hover:text-white text-[10px] tracking-[0.1em] uppercase transition-all">
              <ExternalLink className="w-3 h-3" /> New tab
            </a>
            <a href={cvBlobUrl} download={app.cv.filename} data-testid="cv-download-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00E5FF]/[0.1] text-[#00E5FF] border border-[#00E5FF]/25 hover:bg-[#00E5FF]/[0.15] text-[10px] tracking-[0.1em] uppercase transition-all">
              <Download className="w-3 h-3" /> Download
            </a>
          </div>
        )}
      </div>
      {cvLoading ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 flex justify-center">
          <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
        </div>
      ) : cvError ? (
        <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.05] p-6 text-center">
          <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
          <p className="text-red-300 text-sm">{cvError}</p>
        </div>
      ) : isPdf && cvBlobUrl ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <iframe src={cvBlobUrl} title="CV" className="w-full h-[560px] bg-white" data-testid="cv-preview-frame" />
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
          <FileText className="w-8 h-8 text-white/40 mx-auto mb-3" />
          <p className="text-white/60 text-sm mb-3">Preview not available for this file type.</p>
          <p className="text-white/40 text-xs">Use the buttons above to open or download.</p>
        </div>
      )}
    </div>
  );
};

// ─── Applicant Profile Panel ───
const APP_STATUS_COLORS = {
  new: 'text-[#00E5FF] bg-[#00E5FF]/[0.08] border-[#00E5FF]/25',
  reviewed: 'text-amber-300 bg-amber-500/[0.08] border-amber-500/25',
  shortlisted: 'text-[#c39be5] bg-[#8A2BE2]/[0.10] border-[#8A2BE2]/30',
  hired: 'text-emerald-300 bg-emerald-500/[0.10] border-emerald-500/30',
  rejected: 'text-red-300 bg-red-500/[0.08] border-red-500/25',
};

const ApplicantProfilePanel = ({ profile, loading, currentAppId }) => {
  if (loading) {
    return (
      <div className="p-10 flex justify-center">
        <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
        <User className="w-8 h-8 text-white/20 mx-auto mb-3" />
        <p className="text-white/50 text-sm">No enriched profile available.</p>
      </div>
    );
  }
  const acct = profile.user_account;
  return (
    <div className="space-y-4" data-testid="applicant-profile-panel">
      <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium flex items-center gap-1.5">
        <User className="w-3 h-3" /> Applicant 360
      </p>

      {/* Linked user account */}
      {acct ? (
        <div className="rounded-2xl border border-[#00E5FF]/20 bg-[#00E5FF]/[0.03] p-5">
          <div className="flex items-start gap-4 mb-4">
            {acct.avatar_url ? (
              <img src={acct.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover border border-white/10" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00E5FF]/[0.15] to-[#8A2BE2]/[0.10] border border-white/10 flex items-center justify-center">
                <span className="text-white/60 font-light text-lg">{(acct.first_name || acct.username || 'U').charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium">{acct.first_name} {acct.last_name}</p>
              <p className="text-[#00E5FF] text-[11px]">@{acct.username}</p>
              {acct.bio && <p className="text-white/60 text-xs mt-1.5 leading-relaxed">{acct.bio}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {acct.created_at && (
              <MiniStat icon={Calendar} label="Member since" value={new Date(acct.created_at).toLocaleDateString([], { month: 'short', year: 'numeric' })} />
            )}
            {acct.last_login && (
              <MiniStat icon={Sparkles} label="Last active" value={new Date(acct.last_login).toLocaleDateString([], { month: 'short', day: 'numeric' })} />
            )}
            <MiniStat icon={FileText} label="Lounge posts" value={profile.community?.posts ?? 0} />
            <MiniStat icon={MessageSquare} label="Comments" value={profile.community?.comments ?? 0} />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-white/[0.06]">
            {acct.two_factor_enabled && (
              <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.1em] uppercase font-medium bg-emerald-500/15 border border-emerald-500/25 text-emerald-300">2FA On</span>
            )}
            {acct.policy_accepted_version && (
              <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.1em] uppercase font-medium bg-white/[0.04] border border-white/[0.08] text-white/60">Policies accepted</span>
            )}
            {acct.location && (
              <span className="px-2 py-0.5 rounded-full text-[9px] tracking-[0.1em] uppercase font-medium bg-white/[0.04] border border-white/[0.08] text-white/60 inline-flex items-center gap-1">
                <MapPin className="w-2.5 h-2.5" /> {acct.location}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-center">
          <User className="w-7 h-7 text-white/20 mx-auto mb-2" />
          <p className="text-white/50 text-xs">This applicant doesn't have a Starnight account yet.</p>
          <p className="text-white/30 text-[11px] mt-1">Public applicant — external candidate.</p>
        </div>
      )}

      {/* Application history */}
      <div>
        <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-2.5 flex items-center gap-1.5">
          <Briefcase className="w-3 h-3" /> Application History
          <span className="ml-auto text-white/25 tabular-nums">{profile.totals?.applications || 0}</span>
        </p>
        {(profile.past_applications || []).length === 0 ? (
          <p className="text-white/30 text-xs italic">No history.</p>
        ) : (
          <div className="space-y-2">
            {profile.past_applications.map((p) => (
              <div key={p.id}
                className={`rounded-xl border p-3 flex items-center gap-3 transition-all ${
                  p.is_current
                    ? 'bg-[#00E5FF]/[0.05] border-[#00E5FF]/25'
                    : 'bg-white/[0.02] border-white/[0.06]'
                }`}
                data-testid={`history-app-${p.id}`}>
                <div className="w-8 h-8 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-3.5 h-3.5 text-white/40" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">
                    {p.vacancy_title}
                    {p.is_current && <span className="ml-2 text-[9px] tracking-[0.15em] uppercase text-[#00E5FF]">Current</span>}
                  </p>
                  <p className="text-white/40 text-[10px] mt-0.5">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                  </p>
                </div>
                <span className={`px-2 py-0.5 rounded-md text-[9px] tracking-[0.1em] uppercase font-medium border ${APP_STATUS_COLORS[p.status] || APP_STATUS_COLORS.new}`}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MiniStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg bg-white/[0.02] border border-white/[0.05] px-3 py-2">
    <p className="text-[9px] tracking-[0.12em] uppercase text-white/35 flex items-center gap-1"><Icon className="w-2.5 h-2.5" /> {label}</p>
    <p className="text-white text-xs font-medium mt-0.5 tabular-nums">{value}</p>
  </div>
);

// ─── Notes Panel ───
const NotesPanel = ({ notes, noteBody, setNoteBody, addNote, addingNote, deleteNote, currentUser }) => (
  <div>
    <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-3 flex items-center gap-1.5">
      <MessageSquare className="w-3 h-3" /> Reviewer Notes
      <span className="ml-auto text-white/25 tabular-nums">{notes.length}</span>
    </p>
    {notes.length === 0 ? (
      <p className="text-white/25 text-xs italic mb-3">No notes yet — add the first for your team.</p>
    ) : (
      <div className="space-y-2 mb-4 max-h-[420px] overflow-y-auto pr-1">
        {notes.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(n => (
          <div key={n.note_id} className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3" data-testid={`note-${n.note_id}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#00E5FF]/20 to-[#8A2BE2]/20 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[8px] font-semibold text-white/70">{(n.author_display_name || n.author || 'U').charAt(0).toUpperCase()}</span>
                </div>
                <span className="text-[10px] font-medium text-white/70 truncate">{n.author_display_name || n.author}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[9px] text-white/30 tabular-nums">
                  {n.created_at ? new Date(n.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
                {(n.author === currentUser?.username || currentUser?.role === 'superadmin') && (
                  <button onClick={() => deleteNote(n.note_id)} data-testid={`delete-note-${n.note_id}`}
                    className="text-white/25 hover:text-red-300 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            <p className="text-white/70 text-xs leading-relaxed whitespace-pre-wrap">{n.body}</p>
          </div>
        ))}
      </div>
    )}
    <div className="space-y-1.5">
      <textarea
        value={noteBody}
        onChange={(e) => setNoteBody(e.target.value.slice(0, 3000))}
        placeholder="Add a note for other reviewers…"
        rows={3}
        data-testid="new-note-input"
        className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2 text-white text-xs placeholder-white/25 focus:outline-none focus:border-[#00E5FF]/30 resize-none transition-all"
      />
      <div className="flex items-center justify-between">
        <span className="text-[9px] text-white/25 tabular-nums">{noteBody.length}/3000</span>
        <Button size="sm" onClick={addNote} disabled={addingNote || !noteBody.trim()} data-testid="add-note-btn"
          className="h-7 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase font-medium bg-[#00E5FF]/[0.1] text-[#00E5FF] border border-[#00E5FF]/25 hover:bg-[#00E5FF]/[0.15] disabled:opacity-40">
          {addingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add Note'}
        </Button>
      </div>
    </div>
  </div>
);

const InfoRow = ({ icon: Icon, label, value, link, external }) => (
  <div>
    <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-1 flex items-center gap-1.5">
      <Icon className="w-3 h-3" /> {label}
    </p>
    {link ? (
      <a href={link} target={external ? '_blank' : undefined} rel="noopener noreferrer"
        className="text-white/80 text-sm hover:text-[#00E5FF] transition-colors break-all inline-flex items-center gap-1">
        {value} {external && <ExternalLink className="w-3 h-3 opacity-40" />}
      </a>
    ) : (
      <p className="text-white/80 text-sm break-words">{value}</p>
    )}
  </div>
);

export default VacancyApplicationsPage;
